<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BulkContractServiceEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    items: {
      type: Array,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getPayload = (...args) => {
    const value = item.value.getChanges();
    return _.map(props.items, (p) => ({ id: p.id, value }));
  };

  const formId = ref(null);
  const labelAlign = ref('right');
  const item = ref(null);
  const currencies = ref(null);
  const services = ref(null);
  const invoiceGroups = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'lg',
  }));
  const b_3054425420812800_multiple = computed(() => false);

  const $data = computed(() => ({
    formId: formId.value,
    labelAlign: labelAlign.value,
    item: item.value,
    currencies: currencies.value,
    services: services.value,
    invoiceGroups: invoiceGroups.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3054425420812800_multiple: b_3054425420812800_multiple.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return $fn.value.schemaDefaults('CreateContractService', {
      asResource: true,
    });
  };
  const $$load_currencies = async () => {
    return $fn.value.fetch('list_currency');
  };
  const $$load_services = async () => {
    return $fn.value
      .fetch('suggest_product', {
        parameters: {
          query: '*',
          top: 50,
          skip: 0,
          product_types: 'service',
          product_invoice_groups: 'all',
        },
      })
      .then((data) => _.orderBy(data, [(p) => p.name.toLowerCase()]));
  };
  const $$load_invoiceGroups = async () => {
    return $fn.value.fetch('list_product_invoice_group');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'item' },
      { name: 'currencies' },
      { name: 'services' },
      { name: 'invoiceGroups' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3054425420812800_multiple', nodeId: '3054425420812800' },
    ],
    loaders: {
      formId: $$load_formId,
      item: $$load_item,
      currencies: $$load_currencies,
      services: $$load_services,
      invoiceGroups: $$load_invoiceGroups,
    },
    ux: {},
    dataRefs: {
      formId,
      labelAlign,
      item,
      currencies,
      services,
      invoiceGroups,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3054425420812800_multiple,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'BulkContractServiceEditor',
        'Edit Service Properties'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.items,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('items');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_lqfmq29rp_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_mm7yyeti6_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_request_99k8vqp3y_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_update_contract_service',
      data: getPayload(),
    };
  };
  const act_5731695935601903_setData_tf0tekecx_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.name',
      value: $event.data.option.name,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_5731695935601903_setData_qh7iif2es_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.service_id',
      value: $event.data.option.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_5731695935601903_setData_tcv3hnj24_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'feeItem.unit',
      value: $event.data.option.unit,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_closeModal_lqfmq29rp =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_lqfmq29rp_getActionArgs,
    });
  const inst_evh_7315092382398562_close_45b44wul2 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_lqfmq29rp,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_45b44wul2],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_7t6eea2tz =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_clqdt31wx =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_7t6eea2tz,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_clqdt31wx],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_56iveboc9 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_tqpimsw5q =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_56iveboc9,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_tqpimsw5q],
    event: 'close',
  });
  const act_1040167445267876_closeModal_mm7yyeti6 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_mm7yyeti6_getActionArgs,
    });
  const inst_evh_1040167445267876_success_1ouohgxzo =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_mm7yyeti6,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_99k8vqp3y = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_99k8vqp3y_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_1ouohgxzo],
  });
  const inst_evh_1040167445267876_submit_viaxnukxl =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_99k8vqp3y,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_viaxnukxl],
    event: 'submit',
  });
  const act_5731695935601903_setData_tf0tekecx = new actionTypes.SetDataAction({
    actionArgs: act_5731695935601903_setData_tf0tekecx_getActionArgs,
  });
  const inst_evh_5731695935601903_optionSelected_ftltkxsdl =
    new actionTypes.EventHandlerImpl({
      action: act_5731695935601903_setData_tf0tekecx,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_5731695935601903_setData_qh7iif2es = new actionTypes.SetDataAction({
    actionArgs: act_5731695935601903_setData_qh7iif2es_getActionArgs,
  });
  const inst_evh_5731695935601903_optionSelected_j967nuf8m =
    new actionTypes.EventHandlerImpl({
      action: act_5731695935601903_setData_qh7iif2es,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_5731695935601903_setData_tcv3hnj24 = new actionTypes.SetDataAction({
    actionArgs: act_5731695935601903_setData_tcv3hnj24_getActionArgs,
  });
  const inst_evh_5731695935601903_optionSelected_csoohegcm =
    new actionTypes.EventHandlerImpl({
      action: act_5731695935601903_setData_tcv3hnj24,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_5731695935601903_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_5731695935601903_optionSelected_ftltkxsdl,
        inst_evh_5731695935601903_optionSelected_j967nuf8m,
        inst_evh_5731695935601903_optionSelected_csoohegcm,
      ],
      event: 'optionSelected',
    });
  const act_2434514588044777_closeComponent_j5y7pwuyu =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_ni9o90asn =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_j5y7pwuyu,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_ni9o90asn],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_45b44wul2,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_clqdt31wx,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_tqpimsw5q,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_1ouohgxzo,
    inst_evh_1040167445267876_submit_viaxnukxl,
    save: inst_evh_1040167445267876_submit_viaxnukxl,
    inst_evh_1040167445267876_submit,
    inst_evh_5731695935601903_optionSelected_ftltkxsdl,
    inst_evh_5731695935601903_optionSelected_j967nuf8m,
    inst_evh_5731695935601903_optionSelected_csoohegcm,
    inst_evh_5731695935601903_optionSelected,
    inst_evh_2434514588044777_clickPreventStop_ni9o90asn,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_45b44wul2 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_45b44wul2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_clqdt31wx = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_clqdt31wx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_tqpimsw5q = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_tqpimsw5q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_1ouohgxzo = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_1ouohgxzo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_viaxnukxl = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_viaxnukxl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected_ftltkxsdl = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5731695935601903_optionSelected_ftltkxsdl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected_j967nuf8m = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5731695935601903_optionSelected_j967nuf8m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected_csoohegcm = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5731695935601903_optionSelected_csoohegcm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected = async (event, scope) => {
    return await actions.inst_evh_5731695935601903_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_ni9o90asn = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_ni9o90asn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="BulkContractServiceEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="contract"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <ItpFormGroup
            name="service"
            :label="pgettext('ContractService', 'Service')"
            :label-cols-md="3"
            :content-cols-md="9"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'Select the product or service the invoice line refers to.'
              )
            "
          >
            <ItpSelect2
              name="client"
              v-model="item.service"
              :value="item.service"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.service =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              bind-object
              :options="services"
              textField="name"
              @optionSelected="evh_5731695935601903_optionSelected($event, {})"
            >
            </ItpSelect2>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="3"
            :content-cols-md="9"
            name="invoice_group_id"
            :label="pgettext('ContractService', 'Invoice Group')"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'The invoice group this service belongs to.'
              )
            "
          >
            <ItpFormSelect2
              name="invoice_group_id"
              v-model="item.invoice_group_id"
              :value="item.invoice_group_id"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.invoice_group_id =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              :multiple="b_3054425420812800_multiple"
              :options="invoiceGroups"
              valueField="id"
              textField="name"
              clearable
            >
            </ItpFormSelect2>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="3"
            name="intermediated_service"
            :label="pgettext('ContractService', 'Intermediated service')"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'Select this option if the service is provided via an intermediary party.'
              )
            "
          >
            <ItpFormCheckbox
              name="intermediated_service"
              v-model="item.intermediated_service"
              :value="item.intermediated_service"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.intermediated_service =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
          <ItpFormGroup
            name="valid_to"
            :label="pgettext('ContractService', 'Description')"
            :label-cols-md="3"
            :content-cols-md="9"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'Internal notes for reference only. Not visible externally.'
              )
            "
          >
            <ItpFormTextarea
              name="description"
              v-model="item.description"
              :value="item.description"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.description =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              :rows="5"
            >
            </ItpFormTextarea>
          </ItpFormGroup>
          <hr />
          <fieldset>
            <legend>
              {{ pgettext('ContractServiceEditor|Section', 'Invoicing') }}
            </legend>
            <ItpFormGroup
              name="name"
              :label="pgettext('ContractService', 'Name')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'This text will appear on the invice line.'
                )
              "
            >
              <ItpFormInput
                name="name"
                v-model="item.name"
                :value="item.name"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.name =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              name="invoice_notes"
              :label="pgettext('ContractServiceFee', 'Invoice notes')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'Note to display on the invoice line.'
                )
              "
            >
              <ItpFormTextarea
                name="fee_notes"
                v-model="item.invoice_notes"
                :value="item.invoice_notes"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.invoice_notes =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormTextarea>
            </ItpFormGroup>
            <ItpFormGroup
              name="billing_frequency"
              :label="pgettext('ContractService', 'Billing frequency')"
              :label-cols-md="3"
              :content-cols-md="3"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The cycle length of issuing the invoices.'
                )
              "
            >
              <BillingFrequencySelector
                v-model="item.billing_frequency"
                :value="item.billing_frequency"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.billing_frequency =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </BillingFrequencySelector>
            </ItpFormGroup>
            <ItpFormGroup
              name="billing_date_rule"
              :label="pgettext('ContractService', 'Billing date rule')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The cycle length of issuing the invoices.'
                )
              "
            >
              <BillingDateRuleFields
                v-model="item.billing_date_rule"
                :value="item.billing_date_rule"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.billing_date_rule =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </BillingDateRuleFields>
            </ItpFormGroup>
            <ItpFormGroup :label-cols-md="3" :content-cols-md="9">
              <hr class="dots" />
            </ItpFormGroup>
            <ItpFormGroup
              name="due_date_rule"
              :label="pgettext('ContractService', 'Due date rule')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The rule of calculating the payment dates.'
                )
              "
            >
              <DueDateRuleFields
                v-model="item.due_date_rule"
                :value="item.due_date_rule"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.due_date_rule =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </DueDateRuleFields>
            </ItpFormGroup>
            <ItpFormGroup :label-cols-md="3" :content-cols-md="9">
              <hr class="dots" />
            </ItpFormGroup>
            <ItpFormGroup
              name="billing_currency"
              :label="pgettext('ContractService', 'Billing currency')"
              :label-cols-md="3"
              :content-cols-md="3"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The currency to use on the invoice.'
                )
              "
            >
              <BillingCurrencySelector
                v-model="item.billing_currency"
                :value="item.billing_currency"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.billing_currency =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </BillingCurrencySelector>
            </ItpFormGroup>
            <ItpFormGroup
              name="exchange_rate_source"
              :label="pgettext('ContractService', 'Exchange rate source')"
              :label-cols-md="3"
              :content-cols-md="3"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The currency exchange rate\'s provider.'
                )
              "
            >
              <ExchangeRateSourceSelector
                v-model="item.exchange_rate_source"
                :value="item.exchange_rate_source"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.exchange_rate_source =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ExchangeRateSourceSelector>
            </ItpFormGroup>
            <ItpFormGroup
              name="exchange_rate_date_rule"
              :label="pgettext('ContractService', 'Exchange rate date rule')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The date used to determine the exchange rate for billing purposes.'
                )
              "
            >
              <ExchangeRateDateRuleFields
                v-model="item.exchange_rate_date_rule"
                :value="item.exchange_rate_date_rule"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.exchange_rate_date_rule =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ExchangeRateDateRuleFields>
            </ItpFormGroup>
          </fieldset>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.contract || !forms.contract.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
